/*
 * Copyright Elasticsearch B.V. and/or licensed to Elasticsearch B.V. under one
 * or more contributor license agreements. Licensed under the Elastic License
 * 2.0; you may not use this file except in compliance with the Elastic License
 * 2.0.
 */

// DO NOT MODIFY THIS FILE BY HAND. IT IS GENERATED BY
// `yarn workspace @elastic/esql-definitions generate` FROM
// `elasticsearch/esql/x-pack-esql/definition/functions/mv_in_range.json`.

import type { FunctionDefinition } from '../../definition_types';

/**
 * Returns `true` if at least one value of `field` is within the range `[lower, upper]`, using the natural order of the type. A null or empty field returns `false`, as does a null bound. Both bounds are inclusive by default; set `include_lower` or `include_upper` to `false` in the optional `options` map to make either bound exclusive, covering all four interval forms. Works on any ordered type: numbers, dates, IPs, versions, and strings (compared by their UTF-8 bytes).
 *
 * @example
 * ROW values = [1, 5, 10]
 * | EVAL in_range = mv_in_range(values, 4, 6)
 *
 * @example
 * ROW values = [0, 100]
 * | EVAL in_range = mv_in_range(values, 40, 60)
 *
 * @example
 * ROW words = ["apple", "cherry"]
 * | EVAL in_range = mv_in_range(words, "banana", "date")
 *
 * @example
 * ROW values = [3, 6]
 * | EVAL in_range = mv_in_range(values, 4, 6, {"include_upper": false})
 *
 * @example
 * ROW values = [4, 6]
 * | EVAL in_range = mv_in_range(values, 4, 6, {"include_lower": false, "include_upper": false})
 */
const definition: FunctionDefinition = {
  type: 'scalar',
  name: 'mv_in_range',
  signatures: [
    {
      params: [
        {
          name: 'field',
          type: 'date',
          optional: false,
        },
        {
          name: 'lower',
          type: 'date',
          optional: false,
        },
        {
          name: 'upper',
          type: 'date',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'boolean',
    },
    {
      params: [
        {
          name: 'field',
          type: 'date',
          optional: false,
        },
        {
          name: 'lower',
          type: 'date',
          optional: false,
        },
        {
          name: 'upper',
          type: 'date',
          optional: false,
        },
        {
          name: 'options',
          type: 'function_named_parameters',
          optional: true,
          mapParams: '{name=\'include_lower\', values=[true, false], description=\'Whether the lower bound is inclusive. Defaults to `true`; `false` makes it exclusive (`value > lower`).\', type=[boolean]}, {name=\'include_upper\', values=[true, false], description=\'Whether the upper bound is inclusive. Defaults to `true`; `false` makes it exclusive (`value < upper`).\', type=[boolean]}',
        },
      ],
      variadic: false,
      returnType: 'boolean',
    },
    {
      params: [
        {
          name: 'field',
          type: 'date_nanos',
          optional: false,
        },
        {
          name: 'lower',
          type: 'date_nanos',
          optional: false,
        },
        {
          name: 'upper',
          type: 'date_nanos',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'boolean',
    },
    {
      params: [
        {
          name: 'field',
          type: 'double',
          optional: false,
        },
        {
          name: 'lower',
          type: 'double',
          optional: false,
        },
        {
          name: 'upper',
          type: 'double',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'boolean',
    },
    {
      params: [
        {
          name: 'field',
          type: 'double',
          optional: false,
        },
        {
          name: 'lower',
          type: 'double',
          optional: false,
        },
        {
          name: 'upper',
          type: 'double',
          optional: false,
        },
        {
          name: 'options',
          type: 'function_named_parameters',
          optional: true,
          mapParams: '{name=\'include_lower\', values=[true, false], description=\'Whether the lower bound is inclusive. Defaults to `true`; `false` makes it exclusive (`value > lower`).\', type=[boolean]}, {name=\'include_upper\', values=[true, false], description=\'Whether the upper bound is inclusive. Defaults to `true`; `false` makes it exclusive (`value < upper`).\', type=[boolean]}',
        },
      ],
      variadic: false,
      returnType: 'boolean',
    },
    {
      params: [
        {
          name: 'field',
          type: 'integer',
          optional: false,
        },
        {
          name: 'lower',
          type: 'integer',
          optional: false,
        },
        {
          name: 'upper',
          type: 'integer',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'boolean',
    },
    {
      params: [
        {
          name: 'field',
          type: 'integer',
          optional: false,
        },
        {
          name: 'lower',
          type: 'integer',
          optional: false,
        },
        {
          name: 'upper',
          type: 'integer',
          optional: false,
        },
        {
          name: 'options',
          type: 'function_named_parameters',
          optional: true,
          mapParams: '{name=\'include_lower\', values=[true, false], description=\'Whether the lower bound is inclusive. Defaults to `true`; `false` makes it exclusive (`value > lower`).\', type=[boolean]}, {name=\'include_upper\', values=[true, false], description=\'Whether the upper bound is inclusive. Defaults to `true`; `false` makes it exclusive (`value < upper`).\', type=[boolean]}',
        },
      ],
      variadic: false,
      returnType: 'boolean',
    },
    {
      params: [
        {
          name: 'field',
          type: 'ip',
          optional: false,
        },
        {
          name: 'lower',
          type: 'ip',
          optional: false,
        },
        {
          name: 'upper',
          type: 'ip',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'boolean',
    },
    {
      params: [
        {
          name: 'field',
          type: 'keyword',
          optional: false,
        },
        {
          name: 'lower',
          type: 'keyword',
          optional: false,
        },
        {
          name: 'upper',
          type: 'keyword',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'boolean',
    },
    {
      params: [
        {
          name: 'field',
          type: 'keyword',
          optional: false,
        },
        {
          name: 'lower',
          type: 'keyword',
          optional: false,
        },
        {
          name: 'upper',
          type: 'keyword',
          optional: false,
        },
        {
          name: 'options',
          type: 'function_named_parameters',
          optional: true,
          mapParams: '{name=\'include_lower\', values=[true, false], description=\'Whether the lower bound is inclusive. Defaults to `true`; `false` makes it exclusive (`value > lower`).\', type=[boolean]}, {name=\'include_upper\', values=[true, false], description=\'Whether the upper bound is inclusive. Defaults to `true`; `false` makes it exclusive (`value < upper`).\', type=[boolean]}',
        },
      ],
      variadic: false,
      returnType: 'boolean',
    },
    {
      params: [
        {
          name: 'field',
          type: 'keyword',
          optional: false,
        },
        {
          name: 'lower',
          type: 'keyword',
          optional: false,
        },
        {
          name: 'upper',
          type: 'text',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'boolean',
    },
    {
      params: [
        {
          name: 'field',
          type: 'keyword',
          optional: false,
        },
        {
          name: 'lower',
          type: 'text',
          optional: false,
        },
        {
          name: 'upper',
          type: 'keyword',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'boolean',
    },
    {
      params: [
        {
          name: 'field',
          type: 'keyword',
          optional: false,
        },
        {
          name: 'lower',
          type: 'text',
          optional: false,
        },
        {
          name: 'upper',
          type: 'text',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'boolean',
    },
    {
      params: [
        {
          name: 'field',
          type: 'long',
          optional: false,
        },
        {
          name: 'lower',
          type: 'long',
          optional: false,
        },
        {
          name: 'upper',
          type: 'long',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'boolean',
    },
    {
      params: [
        {
          name: 'field',
          type: 'long',
          optional: false,
        },
        {
          name: 'lower',
          type: 'long',
          optional: false,
        },
        {
          name: 'upper',
          type: 'long',
          optional: false,
        },
        {
          name: 'options',
          type: 'function_named_parameters',
          optional: true,
          mapParams: '{name=\'include_lower\', values=[true, false], description=\'Whether the lower bound is inclusive. Defaults to `true`; `false` makes it exclusive (`value > lower`).\', type=[boolean]}, {name=\'include_upper\', values=[true, false], description=\'Whether the upper bound is inclusive. Defaults to `true`; `false` makes it exclusive (`value < upper`).\', type=[boolean]}',
        },
      ],
      variadic: false,
      returnType: 'boolean',
    },
    {
      params: [
        {
          name: 'field',
          type: 'text',
          optional: false,
        },
        {
          name: 'lower',
          type: 'keyword',
          optional: false,
        },
        {
          name: 'upper',
          type: 'keyword',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'boolean',
    },
    {
      params: [
        {
          name: 'field',
          type: 'text',
          optional: false,
        },
        {
          name: 'lower',
          type: 'keyword',
          optional: false,
        },
        {
          name: 'upper',
          type: 'text',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'boolean',
    },
    {
      params: [
        {
          name: 'field',
          type: 'text',
          optional: false,
        },
        {
          name: 'lower',
          type: 'text',
          optional: false,
        },
        {
          name: 'upper',
          type: 'keyword',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'boolean',
    },
    {
      params: [
        {
          name: 'field',
          type: 'text',
          optional: false,
        },
        {
          name: 'lower',
          type: 'text',
          optional: false,
        },
        {
          name: 'upper',
          type: 'text',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'boolean',
    },
    {
      params: [
        {
          name: 'field',
          type: 'unsigned_long',
          optional: false,
        },
        {
          name: 'lower',
          type: 'unsigned_long',
          optional: false,
        },
        {
          name: 'upper',
          type: 'unsigned_long',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'boolean',
    },
    {
      params: [
        {
          name: 'field',
          type: 'version',
          optional: false,
        },
        {
          name: 'lower',
          type: 'version',
          optional: false,
        },
        {
          name: 'upper',
          type: 'version',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'boolean',
    },
  ],
  preview: true,
  snapshotOnly: false,
};

export default definition;
